import { useEffect, useRef, useState } from 'react';

import ModalHeaderDs, {
  ModalHeaderDsProps,
} from '@/components/layout/modals/ModalHeader.ds';
import ModalFooterDs, { ModalFooterDsProps } from '@/components/layout/modals/ModalFooter.ds';

import styles from './CenteredModal.module.scss';

export type CenteredModalProps = {
  id?: string;
  opened: boolean;
  title?: string;
  customContent?: any;
  cancelAction?: () => void;
  toggle?: () => void;
  fullWidth?: boolean;
  fullHeight?: boolean;
  customClassname?: string;
  noPadding?: boolean;
  padding?: 'no-padding' | 'large';
  withHeading?: boolean;
  withScrollbar?: boolean;
  defaultFooterProps?: ModalFooterDsProps;
  customFooter?: JSX.Element;
  whichHeaderButton?: 'closeButton' | 'returnButton' | 'bothButtons';
  shouldCenterTitle?: boolean
}

export const CenteredModal = ({
  id,
  opened,
  title,
  customContent,
  cancelAction,
  toggle,
  fullWidth = false,
  fullHeight = false,
  customClassname,
  noPadding,
  withHeading = true,
  whichHeaderButton = 'closeButton',
  defaultFooterProps,
  customFooter,
  shouldCenterTitle = false,
  padding
}: CenteredModalProps) => {
  const bodyContainerRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  const computedClassName = [
    styles.modal,
    customClassname,
    fullWidth && styles['full-width'],
    fullHeight && styles['full-height'],
    noPadding && styles['no-padding'],
  ].filter(Boolean).join(' ');

  const bodyComputedClassName = [
    styles['modal-body']
  ].filter(Boolean).join(' ');

  useEffect(() => {
    if (bodyContainerRef.current) {
      const visibleHeight = bodyContainerRef.current.clientHeight;
      const scrollHeight = bodyContainerRef.current.scrollHeight;
      setShouldScroll(scrollHeight > visibleHeight);
    }
  }, [bodyContainerRef.current]);

  const headerProps = (() => {
    switch (whichHeaderButton) {
      case 'closeButton':
        return {
          whichButton: 'closeButton',
          onCloseClick: cancelAction,
          title: title,
          centeredTitle: shouldCenterTitle,
        };
      case 'returnButton':
        return {
          whichButton: 'returnButton',
          onReturnClick: cancelAction,
        };
      case 'bothButtons':
        return {
          whichButton: 'bothButtons',
          onCloseClick: cancelAction,
          onReturnClick: cancelAction,
          title: title,
        };
    }
  })() as ModalHeaderDsProps;

  return opened && (
    <>
      <div
        className={styles['container-overlay']}
        onClick={toggle ? () => toggle() : (cancelAction ? () => cancelAction() : undefined)}
        data-test-id="modal-container-overlay"
        data-rbf={id ? `${id}-modal-container-overlay` : 'modal-container-overlay'}
      />
      <dialog id={id} data-test-id="modal" className={computedClassName} open={opened}>
        {withHeading && (
          <div data-test-id="modal-header">
            <ModalHeaderDs {...headerProps} size={padding} />
          </div>
        )}

        {!!customContent && (
          <section className={bodyComputedClassName} ref={bodyContainerRef}>
            {customContent}
          </section>
        )}

        {!!defaultFooterProps && !customFooter && (
          <ModalFooterDs
            sticky={shouldScroll} groupVariant={defaultFooterProps.groupVariant} direction={defaultFooterProps.direction}
            confirm={defaultFooterProps.confirm} cancel={defaultFooterProps.cancel}
          />
        )}

        {!!customFooter && !defaultFooterProps && (
          <div data-test-id="modal-footer">
            {customFooter}
          </div>
        )}
      </dialog>
    </>
  );
};

export default CenteredModal;
